// BuildUpPage.js - WITH AI-GENERATED IMAGES
// Complete implementation using Pollinations.AI (100% free, no signup)

import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchBuildUpByCategory } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import './PageLayout.css';
import "../components/loading.css";

// ✅ NEW: Helper to generate category-based placeholder icons
const getCategoryIcon = (categoryName) => {
  const name = (categoryName || '').toLowerCase();
  
  if (name.includes('piling') || name.includes('pile')) return '🏗️';
  if (name.includes('foundation')) return '🧱';
  if (name.includes('concrete')) return '🏭';
  if (name.includes('steel') || name.includes('metal')) return '⚙️';
  if (name.includes('timber') || name.includes('wood')) return '🌲';
  if (name.includes('insulation')) return '🏠';
  if (name.includes('window') || name.includes('glazing')) return '🪟';
  if (name.includes('roof') || name.includes('tile')) return '🏘️';
  if (name.includes('door')) return '🚪';
  if (name.includes('paint') || name.includes('coating')) return '🎨';
  if (name.includes('floor')) return '◼️';
  if (name.includes('wall')) return '▢';
  
  return '📦'; // Default
};

// ✅ NEW: Generate placeholder colors for categories
const getPlaceholderColor = (categoryName) => {
  const name = (categoryName || '').toLowerCase();
  
  if (name.includes('piling')) return '#3B82F6';
  if (name.includes('foundation')) return '#8B5CF6';
  if (name.includes('concrete')) return '#6B7280';
  if (name.includes('steel')) return '#374151';
  if (name.includes('timber')) return '#92400E';
  if (name.includes('insulation')) return '#EC4899';
  if (name.includes('window')) return '#06B6D4';
  if (name.includes('roof')) return '#DC2626';
  if (name.includes('paint')) return '#F59E0B';
  
  return '#D1D5DB'; // Default gray
};

// ✅ NEW: Generate Pollinations.AI image URL
const getMaterialImageUrl = (materialName, categoryName, description, unit) => {
  if (!materialName) return null;
  
  // ✅ Build comprehensive prompt from all available fields
  let promptParts = [];
  
  // Add category if available
  if (categoryName) {
    promptParts.push(categoryName);
  }
  
  // Add material name (most important)
  promptParts.push(materialName);
  
  // Add unit/specification if available
  if (unit) {
    promptParts.push(unit);
  }
  
  // Add key details from description (first 50 characters)
  if (description) {
    const descShort = description
      .replace(/\(.*?\)/g, '') // Remove parentheses content
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
      .split(' ')
      .slice(0, 8) // Take first 8 words
      .join(' ');
    if (descShort) {
      promptParts.push(descShort);
    }
  }
  
  // Combine into full prompt
  const fullPrompt = promptParts.join(' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  
  // Create consistent seed from material name for caching
  const seed = materialName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  console.log('🎨 AI Prompt:', fullPrompt); // ✅ Debug: see what's being generated
  
  return `https://image.pollinations.ai/prompt/${encodeURIComponent(fullPrompt)}?width=100&height=100&nologo=true&seed=${seed}&model=flux`;
};


// ✅ NEW: Material Image Component with fallback
// ✅ FIXED: Lazy Loading Image Component
const LazyMaterialImage = ({ materialName, categoryName, description, unit }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef(null);
  
  const fallbackIcon = getCategoryIcon(categoryName);
  const fallbackColor = getPlaceholderColor(categoryName);
  
  // ✅ Intersection Observer
  useEffect(() => {
    if (!imgRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          console.log('👀 Image became visible:', materialName); // Debug
          setIsVisible(true);
          setIsLoading(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.01 }
    );
    
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [materialName]);
  
  // ✅ Generate URL when visible
  const imageUrl = isVisible ? getMaterialImageUrl(materialName, categoryName, description, unit) : null;
  
  // ✅ FIXED: Show error fallback (but keep trying to load if not visible yet)
  if (imageError) {
    return (
      <div 
        className="material-thumbnail fallback"
        style={{
          backgroundColor: fallbackColor,
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          border: '1px solid rgba(0,0,0,0.1)'
        }}
        title={materialName}
      >
        {fallbackIcon}
      </div>
    );
  }
  
  // ✅ FIXED: Show loading state with ref attached
  return (
    <div 
      ref={imgRef} 
      style={{ 
        position: 'relative', 
        width: '50px', 
        height: '50px',
        minHeight: '50px' // Prevent collapse
      }}
    >
      {/* Show icon while waiting for intersection OR while loading */}
      {(!isVisible || isLoading) && (
        <div style={{
          position: isVisible ? 'absolute' : 'relative',
          width: '50px',
          height: '50px',
          backgroundColor: fallbackColor,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isVisible ? 0.5 : 1,
          border: '1px solid rgba(0,0,0,0.1)'
        }}>
          {fallbackIcon}
        </div>
      )}
      
      {/* Only render img tag after intersection */}
      {isVisible && imageUrl && (
        <img
          src={imageUrl}
          alt={materialName}
          onLoad={() => {
            console.log('✅ Image loaded:', materialName);
            setIsLoading(false);
          }}
          onError={(e) => {
            console.error('❌ Image failed:', materialName, e);
            setImageError(true);
            setIsLoading(false);
          }}
          loading="lazy"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: isLoading ? 'none' : 'block',
            width: '50px',
            height: '50px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}
        />
      )}
    </div>
  );
};


// ========== MAIN COMPONENT (unchanged except for image column) ==========
function BuildUpPage() {
  const { quantityCatId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);

  // Pagination — render in pages so huge categories (10k+ EPDs) don't freeze the DOM.
  // Filtering/search still runs over the FULL list; only the rendered slice is paged.
  const PAGE_SIZE = 1000;
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`buildUpFilters_${quantityCatId}`);
      return saved ? JSON.parse(saved) : { category: '', name: '' };
    } catch {
      return { category: '', name: '' };
    }
  });

  const [priorityCategories, setPriorityCategories] = useState([]);

  const { user } = useContext(AuthContext);
  const role = (user?.role || 'user').toLowerCase();
  const isAdmin = role === 'admin';
  const isUser  = role === 'user';

  // Fetch data
  useEffect(() => {
    let isMounted = true;
    const MIN_SPINNER_MS = 2000;
    const start = Date.now();

    (async () => {
      try {
        const res = await fetchBuildUpByCategory(quantityCatId, user?.email || '', role);

        let materialsData;
        if (res?.data && Array.isArray(res.data)) materialsData = res.data;
        else if (Array.isArray(res)) materialsData = res;
        else materialsData = [];

        if (!isMounted) return;

        // ✅ Sort by CategoryPriority so ordering is consistent across all regions
        materialsData.sort((a, b) => {
          const pA = a.CategoryPriority ?? Number.MAX_SAFE_INTEGER;
          const pB = b.CategoryPriority ?? Number.MAX_SAFE_INTEGER;
          if (pA !== pB) return pA - pB;
          return (a.CategoryName || '').localeCompare(b.CategoryName || '');
        });

        setMaterials(materialsData);
        setFilteredMaterials(materialsData);

        const categoriesWithPriority = materialsData
          .filter(m => m.CategoryName && m.CategoryPriority !== null && m.CategoryPriority !== undefined)
          .map(m => ({
            id: m.CategoryId,
            name: m.CategoryName,
            parentCategoryId: m.ParentCategoryId,
            priority: m.CategoryPriority
          }))
          .reduce((acc, cur) => acc.some(x => x.id === cur.id) ? acc : acc.concat(cur), [])
          .sort((a, b) => (a.priority - b.priority) || a.name.localeCompare(b.name));

        setPriorityCategories(categoriesWithPriority);

      } catch (err) {
        console.error('Error fetching build-up data:', err);
        if (!isMounted) return;
        setMaterials([]);
        setFilteredMaterials([]);
      } finally {
        const elapsed = Date.now() - start;
        const remaining = Math.max(0, MIN_SPINNER_MS - elapsed);
        setTimeout(() => { if (isMounted) setLoading(false); }, remaining);
      }
    })();

    return () => { isMounted = false; };
  }, [quantityCatId]);

  // Filters
  useEffect(() => {
    if (!Array.isArray(materials)) {
      setFilteredMaterials([]);
      return;
    }

    let filtered = materials;

    if (filters.category) {
      filtered = filtered.filter(m =>
        m.CategoryName && m.CategoryName.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    if (filters.name) {
      filtered = filtered.filter(m =>
        m.QuantityUnitName && m.QuantityUnitName.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    setFilteredMaterials(filtered);
    setCurrentPage(1); // reset to first page whenever the filtered set changes
  }, [filters, materials]);

  const handleFilterChange = (type, value) => {
    const next = { ...filters, [type]: value };
    setFilters(next);
    try { sessionStorage.setItem(`buildUpFilters_${quantityCatId}`, JSON.stringify(next)); } catch {}
  };

  const clearFilters = () => {
    const cleared = { category: '', name: '' };
    setFilters(cleared);
    try { sessionStorage.setItem(`buildUpFilters_${quantityCatId}`, JSON.stringify(cleared)); } catch {}
  };

  const formatToThreeDecimals = (v) => (v === null || v === undefined || v === '' ? '' : (isNaN(+v) ? v : (+v).toFixed(3)));
  const formatRValue = (v) => (v === null || v === undefined || v === '' ? '-' : (isNaN(+v) ? '-' : (+v).toFixed(2)));

  const handleBreakdownClick = (quantityUnitId, e) => {
    // If Ctrl/Cmd key is pressed, let the browser handle it (open in new tab)
    if (e?.ctrlKey || e?.metaKey) return;
    
    // Otherwise, normal navigation
    e?.preventDefault();
    navigate(`/breakdown/${quantityUnitId}`);
    };
  const handleAdminEdit = (row, e) => {
    if (e?.ctrlKey || e?.metaKey) return;
    
    e?.preventDefault();
    navigate('/materialeditor', { state: { materialId: row.QuantityUnitId } });
  };

  const handleUserEdit = (row, e) => {
    if (e?.ctrlKey || e?.metaKey) return;
    
    e?.preventDefault();
    navigate('/composite-requests', { state: { prefill: { materialId: row.QuantityUnitId, materialName: row.QuantityUnitName || null } } });
  };
  const handleBack = () => navigate(-1);

  const safeFilteredMaterials = Array.isArray(filteredMaterials) ? filteredMaterials : [];

  // Page the filtered list (search covers everything; only the current page renders).
  const totalPages  = Math.max(1, Math.ceil(safeFilteredMaterials.length / PAGE_SIZE));
  const safePage    = Math.min(currentPage, totalPages);
  const pageStart   = (safePage - 1) * PAGE_SIZE;
  const currentPageMaterials = safeFilteredMaterials.slice(pageStart, pageStart + PAGE_SIZE);

  if (loading) {
    return (
      <div className="supplier-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Materials...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* TOP BAR */}
      <div className="page-toolbar">
        <button className="back-btn" onClick={handleBack}>← Back</button>
        <h1 className="page-title">{quantityCatId}</h1>
        <div className="page-actions" />
      </div>

      {/* FILTERS */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="category-filter">Category</label>
            <select
              id="category-filter"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="">All Categories</option>
              {priorityCategories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name} {category.parentCategoryId && `(${category.parentCategoryId})`}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="name-filter">Name</label>
            <input
              id="name-filter"
              type="text"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              placeholder="Search by name..."
              className="filter-select"
            />
          </div>

          <div className="filter-actions">
            <button
              onClick={clearFilters}
              className="clear-filters-btn"
              disabled={!filters.category && !filters.name}
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="filter-results">
          Showing {safeFilteredMaterials.length} of {Array.isArray(materials) ? materials.length : 0} items
          {filters.category && (
            <span style={{ marginLeft: '10px', color: '#666' }}>
              (filtered by: {filters.category})
            </span>
          )}
        </div>
      </div>

      {/* TABLE */}
      {(!Array.isArray(materials) || materials.length === 0) ? (
        <div className="loading-state">
          <p>No build-up data found.</p>
        </div>
      ) : (
        <table className="supply-table">
          <thead>
            <tr>
              {/* ✅ NEW: Image column */}
              <th style={{ width: '60px' }}>Image</th>
              <th style={{ width: '11%' }}>Category</th>
              <th style={{ width: '22%' }}>Name</th>
              <th style={{ width: '15%' }}>Description</th>
              <th style={{ width: '6%' }}>Unit</th>
              {isAdmin && <th style={{ width: '7%' }}>Rate ($)</th>}
              <th style={{ width: '8%' }}>Co2 (kg/Co2)</th>
              <th style={{ width: '8%' }}>Biogenic (kg/Co2)</th>
              <th style={{ width: '7%' }}>Weight (kg)</th>
              <th style={{ width: '6%' }}>R-Value</th>
              <th style={{ width: '110px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentPageMaterials.map((m, i) => (
              <tr key={i}>
                {/* ✅ NEW: Image cell with AI-generated image */}
                <td className="image-cell" style={{ textAlign: 'center', padding: '8px' }}>
                  <LazyMaterialImage  
                    materialName={m.QuantityUnitName} 
                    categoryName={m.CategoryName} 
                    description={m.Description}
                    unit={m.QuantityTypeName}
                  />
                </td>
                
                <td>
                  {m.CategoryName || m.CategoryId}
                  {process.env.NODE_ENV === 'development' && m.CategoryPriority && (
                    <small style={{ color: '#999', display: 'block', fontSize: '0.7em' }}>
                    </small>
                  )}
                </td>
                <td>
                  {m.QuantityUnitName}
                  {m.PrivateOwnerEmail && (
                    <span style={{
                      marginLeft: 6,
                      fontSize: 11,
                      background: '#fef3c7',
                      color: '#92400e',
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontWeight: 'bold'
                    }}>
                      🔒 Private
                    </span>
                  )}
                </td>
                <td>{m.Description}</td>
                <td>{m.QuantityTypeName}</td>
                {isAdmin && <td>{formatToThreeDecimals(m.vquest_Rate)}</td>}
                <td>{formatToThreeDecimals(m.vquest_Co2)}</td>
                <td>{formatToThreeDecimals(m.vquest_Co2_Biogenic)}</td>
                <td>{formatToThreeDecimals(m.vquest_Weight)}</td>
                <td className={m.RValue ? 'rvalue-cell has-value' : 'rvalue-cell no-value'}>
                  {formatRValue(m.RValue)}
                </td>
                <td className="action-cell">
                  <div className="action-buttons action-buttons--stacked">
                    {/* Breakdown button as Link */}
                    <Link 
                      to={`/breakdown/${m.QuantityUnitId}`}
                      onClick={(e) => handleBreakdownClick(m.QuantityUnitId, e)}
                      className="action-button action-button--breakdown"
                      style={{ 
                        textDecoration: 'none',
                        display: 'inline-block' }}
                    >
                      Breakdown
                    </Link>
                    
                    {/* Admin Edit button - pass materialId in URL */}
                    {isAdmin && (
                      <Link
                        to={`/materialeditor?materialId=${m.QuantityUnitId}`}
                        onClick={(e) => handleAdminEdit(m, e)}
                        className="action-button action-button--edit"
                      >
                        Edit
                      </Link>
                    )}

                    {/* User Create Copy button — compact copy icon */}
                    {isUser && (
                      <Link
                        to={`/composite-requests?materialId=${m.QuantityUnitId}&materialName=${encodeURIComponent(m.QuantityUnitName || '')}`}
                        onClick={(e) => handleUserEdit(m, e)}
                        className="action-button action-button--edit action-button--icon"
                        title="Create Copy"
                        aria-label="Create Copy"
                      >
                        ⧉
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, margin: '18px 0', flexWrap: 'wrap' }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            style={{ padding: '8px 14px', border: '1px solid #ccc', borderRadius: 6, background: safePage <= 1 ? '#e9ecef' : '#fff', cursor: safePage <= 1 ? 'not-allowed' : 'pointer', fontSize: 14 }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 14, color: '#555' }}>
            Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
            <span style={{ color: '#888', marginLeft: 8 }}>
              ({pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, safeFilteredMaterials.length)} of {safeFilteredMaterials.length})
            </span>
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            style={{ padding: '8px 14px', border: '1px solid #ccc', borderRadius: 6, background: safePage >= totalPages ? '#e9ecef' : '#fff', cursor: safePage >= totalPages ? 'not-allowed' : 'pointer', fontSize: 14 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default BuildUpPage;
